<template>
    <div
        class="EntitiesTable"
        :class="{ 'EntitiesTable--with-custom-property': displayedCustomProperties.length > 0 }"
    >
        <InfiniteScroll @scroll="$emit('scroll-end')">
            <Table>
                <template #head>
                    <HeadCell
                        v-if="rbacShowBulkActionCheckbox"
                        class="EntitiesTable__selectionHeader"
                    >
                        <Checkbox
                            :disabled="entities.length === 0 || isPerformingBulkAction"
                            :model-value="areAllSelected"
                            @update:model-value="toggleSelection"
                        />
                    </HeadCell>
                    <HeadCell
                        class="EntitiesTable__nameHeader"
                        sortable
                        sort-type="alpha"
                        :sort="getSort('displayName')"
                        @sort="changeSort('displayName', $event)"
                    >
                        Entity
                    </HeadCell>
                    <HeadCell
                        class="EntitiesTable__effectiveGroupHeader"
                    >
                        Risk Profile
                    </HeadCell>
                    <HeadCell
                        v-for="customProperty in displayedCustomProperties"
                        :key="customProperty.id"
                        v-tooltip="{ content: customProperty.name, delay: { show: 1000 } }"
                        class="EntitiesTable__customPropertyHeader"
                    >
                        {{ customProperty.name }}
                    </HeadCell>
                    <HeadCell
                        class="EntitiesTable__expirationHeader"
                        sortable
                        :sort="getSort('nextExpiration')"
                        @sort="changeSort('nextExpiration', $event)"
                    >
                        Expiration Date
                    </HeadCell>
                    <HeadCell
                        class="EntitiesTable__complianceStatusHeader"
                        sortable
                        :sort="getSort('complianceStatus')"
                        @sort="changeSort('complianceStatus', $event)"
                    >
                        Compliance Status
                    </HeadCell>
                    <HeadCell class="EntitiesTable__verificationStatusHeader">
                        Verification Status
                    </HeadCell>
                    <HeadCell
                        v-if="rbacShowActionButton"
                        class="EntitiesTable__actionsHeader"
                    />
                </template>

                <template #toolbar>
                    <div class="EntitiesTable__toolbar">
                        <BulkActionsExpandable
                            :enable-send-document-request="isEnabledSendDocumentRequest"
                            :disabled="selectedEntities.length === 0
                                || currentlyUpdatedEntitiesIds.length > 0
                                || !hasUsableBulkAction"
                            :loading="isPerformingBulkAction"
                            :show-pause="showBulkActionsPause"
                            :show-unpause="showBulkActionsUnpause"
                            :show-activate="showBulkActionsActivate"
                            :show-deactivate="showBulkActionsDeactivate"
                            @bulk-activate="bulkActivateEntities"
                            @bulk-deactivate="bulkDeactivateEntities"
                            @bulk-set-pause-state="bulkSetEntitiesPauseStatus"
                            @send-document-request="sendDocumentRequest"
                        />
                        <slot name="toolbar" />
                    </div>
                </template>

                <template #toolbarEnd>
                    <RbacDisplayGuard :features="[RbacFeature.dashboardUploadContract]">
                        <NewButton
                            v-if="showContractUploadButton"
                            class="mr-2"
                            variant="outline"
                            color="primary"
                            @click="$emit('openUploadContractModal')"
                        >
                            <EidIcon :icon-src="faUploadContract" />
                            Upload Contract
                        </NewButton>
                    </RbacDisplayGuard>

                    <RbacDisplayGuard :features="[RbacFeature.dashboardDownloadData]">
                        <NewButton
                            v-tooltip="{content: 'This may take up to one minute…', shown: exporting, triggers: []}"
                            class="mr-2"
                            variant="outline"
                            color="neutral"
                            :loading="exporting"
                            @click="$emit('export')"
                        >
                            <EidIcon :icon-src="faDownload" />
                            Download Data
                        </NewButton>
                    </RbacDisplayGuard>

                    <RbacDisplayGuard :features="[RbacFeature.dashboardAddAndEditEntity]">
                        <Expandable
                            label="Add/Import Entity"
                            :icon="faPlus"
                            :menu-config="{
                                positionX: 'end',
                                autoFlip: 'y',
                                flipEstimatedHeight: 100,
                            }"
                            :menu-items="addEntityMenuItems"
                        />
                    </RbacDisplayGuard>
                </template>
                <EntitiesTableRow
                    v-for="entity of entities"
                    :key="entity.id"
                    :entity="entity"
                    :is-performing-bulk-action="isPerformingBulkAction"
                    :displayed-custom-properties="displayedCustomProperties"
                    :currently-updated-entities-ids="currentlyUpdatedEntitiesIds"
                    :selected="selectedIds.has(entity.id)"
                    :is-loading-submission-link="isLoadingSubmissionLink"
                    :submission-link="getSubmissionLink(entity.id)"
                    :rbac-show-action-button="rbacShowActionButton"
                    :rbac-show-bulk-action-checkbox="rbacShowBulkActionCheckbox"
                    v-bind="$attrs"
                    @selection-change="changeSelection"
                    @set-entities-active-state="$emit('setEntitiesActiveState', $event)"
                    @set-entities-pause-state="$emit('setEntitiesPauseState', $event)"
                    @send-document-request="$emit('sendDocumentRequest', $event)"
                />
                <template v-if="entities.length === 0 && finished" #message>
                    <slot name="message">
                        <NoEntitiesFound @reset="$emit('clearFilters')" />
                    </slot>
                </template>
                <template v-else-if="entities.length > 0 && finished" #status>
                    <span>All Entities Displayed.</span>
                </template>
                <template v-else-if="loading" #status>
                    <LineLoader />
                    <span>Loading...</span>
                </template>
            </Table>
        </InfiniteScroll>
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { faUpload } from '@fortawesome/free-solid-svg-icons';
    import { faArrowUpFromBracket, faDownload, faPlus } from '@fortawesome/pro-light-svg-icons';

    import { Checkbox } from '@evidentid/dashboard-commons/components/Checkbox';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import InfiniteScroll from '@evidentid/dashboard-commons/components/InfiniteScroll/InfiniteScroll.vue';
    import { LineLoader } from '@evidentid/dashboard-commons/components/LineLoader';
    import { HeadCell, Table } from '@evidentid/dashboard-commons/components/Table';
    import { Todo } from '@evidentid/dashboard-commons/components/Todo';
    import { useUserStore } from '@evidentid/dashboard-commons/modules/auth/store/user';
    import RbacDisplayGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import {
        hasSufficientFeatures,
    } from '@evidentid/dashboard-commons/modules/role-base-access-control/utils/has-sufficient-features/hasSuffiencientFeatures';
    import {
        CustomProperty,
        Entity,
    } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import {
        Sorting,
        SortingDirection,
    } from '@evidentid/universal-framework/sorting';

    import Expandable from '@/components/expandable/Expandable.vue';
    import NewButton from '@/components/new-button/NewButton.vue';
    import { ExpandableMenuItem } from '@/models/ExpandableMenuItemConfig.model';
    import {
        DASHBOARD_ACTION_BUTTON_REQUIRED_ONE_OF_FEATURES,
    } from '@/modules/dashboard/constants/dashboardActionButtonRequiredOneOfFeatures.constant';
    import {
        DASHBOARD_BULK_ACTION_REQUIRED_ONE_OF_FEATURES,
    } from '@/modules/dashboard/constants/dashboardBulkActionRequiredOneOfFeatures.constant';
    import NoEntitiesFound from '@/modules/entity-filtering/components/NoEntitiesFound/NoEntitiesFound.vue';
    import BulkActionsExpandable
        from '@/modules/entity-management/components/BulkActionsExpandable/BulkActionsExpandable.vue';
    import { isEligibleForNewLink } from '@/utils/is-eligible-for-new-link/isEligibleForNewLink';

    import EntitiesTableRow from '../EntitiesTableRow/EntitiesTableRow.vue';

    // PRODUCT-26110 Task 13 follow-up: declares every event this component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'scroll-end' but
    // it is neither declared in the emits option nor as an 'onScrollEnd' prop" every
    // time it fires (seen x22 in live QA; `scroll-end` is forwarded here from
    // InfiniteScroll's own `scroll` event).
    @Component({
        components: {
            RbacDisplayGuard, BulkActionsExpandable, Checkbox, EidIcon, NoEntitiesFound, EntitiesTableRow, HeadCell,
            Todo, Table, LineLoader, NewButton, Expandable, InfiniteScroll,
        },
        emits: [
            'openUploadContractModal', 'export', 'clearFilters', 'openAddIndividualEntityModal',
            'openBulkImportEntitiesModal', 'sort', 'setEntitiesActiveState', 'setEntitiesPauseState',
            'sendDocumentRequest', 'scroll-end',
        ],
    })
    export default class EntitiesTable extends Vue {
        @Prop({ type: Boolean, default: false })
        private finished!: boolean;

        @Prop({ type: Boolean, default: false })
        private loading!: boolean;

        @Prop({ type: Boolean, default: false })
        private exporting!: boolean;

        @Prop({ type: Array, default: () => [] })
        private entities!: Entity[];

        @Prop({ type: Object })
        private sort!: Sorting;

        @Prop({ type: Array, default: () => [] })
        private displayedCustomProperties!: CustomProperty[];

        @Prop({ type: Array, default: () => [] })
        private currentlyUpdatedEntitiesIds!: string[];

        @Prop({ type: Boolean, default: false })
        private isPerformingBulkAction!: boolean;

        @Prop({ type: Boolean, default: false })
        private isLoadingSubmissionLink!: boolean;

        @Prop({ type: Object, default: {} })
        private submissionLinkPerEntityId!: Record<string, string>;

        @Prop({ type: Boolean, default: false })
        private showContractUploadButton!: boolean;

        private RbacFeature = RbacFeature;
        private faPlus = faPlus;
        private faUpload = faUpload;
        private faDownload = faDownload;
        private faUploadContract = faArrowUpFromBracket;

        private selectedIds: Set<string> = new Set<string>();

        // PRODUCT-26110 Phase 2B2: `selectedEntities` is DERIVED from the live `entities`
        // list + `selectedIds`, not stored. `patchEntitiesInList` swaps in fresh entity
        // objects at the SAME ids (e.g. bulk pause/unpause), so a stored copy would keep
        // the old `paused`/`active` flags and the bulk-action buttons + emitted payloads
        // would go stale. Deriving it also avoids deep-watching the array: `@Watch('entities')`
        // would have been forced `deep: true` by @vue/compat's WATCH_ARRAY (since removed
        // in PRODUCT-26135) and would have synchronously `traverse()`d the ~1000 reactive
        // entity objects -> main-thread FREEZE.
        private get selectedEntities(): Entity[] {
            return this.entities.filter((entity) => this.selectedIds.has(entity.id));
        }

        // A primitive id-key (string), NOT the array, so compat's WATCH_ARRAY (since
        // removed) couldn't have forced a deep traverse. Changes only when the set of
        // entity ids changes.
        private get entitiesSelectionKey(): string {
            return this.entities.map((entity) => entity.id).join(',');
        }

        // Prune ids whose entities have left the list (filter / refresh / pagination).
        @Watch('entitiesSelectionKey')
        private onEntityIdsChange() {
            const presentIds = new Set<string>(this.entities.map((entity) => entity.id));
            const remaining = [ ...this.selectedIds ].filter((id) => presentIds.has(id));
            if (remaining.length !== this.selectedIds.size) {
                this.selectedIds = new Set<string>(remaining);
            }
        }

        private get addEntityMenuItems(): ExpandableMenuItem[] {
            return [
                {
                    label: 'Add Entity',
                    icon: this.faPlus,
                    action: () => this.$emit('openAddIndividualEntityModal'),
                },
                {
                    label: 'Bulk Import Entities',
                    icon: this.faUpload,
                    action: () => this.$emit('openBulkImportEntitiesModal'),
                },
            ];
        }

        private get areAllSelected(): boolean {
            return this.entities.length > 0 ? this.entities.length === this.selectedEntities.length : false;
        }

        private get showBulkActionsActivate(): boolean {
            return this.selectedEntities.some((entity) => !entity.active);
        }

        private get showBulkActionsDeactivate(): boolean {
            return this.selectedEntities.some((entity) => entity.active);
        }

        private get showBulkActionsPause(): boolean {
            return this.selectedEntities.some((entity) => entity.active && !entity.paused);
        }

        private get showBulkActionsUnpause(): boolean {
            return this.selectedEntities.some((entity) => entity.active && entity.paused);
        }

        // Whether at least one bulk action would actually be usable for this user and
        // selection -- as opposed to `selectedEntities.length > 0`, which is true even when
        // every action is either hidden by RBAC or rendered disabled, opening the bar onto an
        // empty or all-greyed-out menu.
        private get hasUsableBulkAction(): boolean {
            const features = useUserStore().currentRpRbacFeatures;
            const hasPauseFeature = hasSufficientFeatures([ RbacFeature.dashboardPauseEntity ], features);
            const hasDeactivateFeature = hasSufficientFeatures(
                [ RbacFeature.dashboardActivateDeactivateEntity ], features);
            const hasCreateNewLinkFeature = hasSufficientFeatures([ RbacFeature.dashboardCreateNewLink ], features);
            return (hasPauseFeature && (this.showBulkActionsPause || this.showBulkActionsUnpause))
                || (hasDeactivateFeature && (this.showBulkActionsActivate || this.showBulkActionsDeactivate))
                || (hasCreateNewLinkFeature && this.isEnabledSendDocumentRequest);
        }

        private get rbacShowActionButton(): boolean {
            return hasSufficientFeatures(
                DASHBOARD_ACTION_BUTTON_REQUIRED_ONE_OF_FEATURES,
                useUserStore().currentRpRbacFeatures,
                true,
            );
        }

        private get rbacShowBulkActionCheckbox(): boolean {
            return hasSufficientFeatures(
                DASHBOARD_BULK_ACTION_REQUIRED_ONE_OF_FEATURES,
                useUserStore().currentRpRbacFeatures,
                true,
            );
        }

        private changeSort(column: string, direction: SortingDirection): void {
            this.$emit('sort', { column, direction });
        }

        private getSort(column: string): SortingDirection | undefined {
            return this.sort?.column === column ? this.sort?.direction : undefined;
        }

        private toggleSelection(value: boolean): void {
            this.selectedIds = value
                ? new Set<string>(this.entities.map((entity) => entity.id))
                : new Set<string>();
        }

        private getSubmissionLink(entityId: string): string {
            return this.submissionLinkPerEntityId[entityId] || '';
        }

        private changeSelection(entity: Entity): void {
            this.selectedIds = new Set<string>(this.selectedIds);
            this.selectedIds.has(entity.id) ? this.deselectEntity(entity) : this.selectEntity(entity);
        }

        private deselectEntity(entity: Entity): void {
            this.selectedIds.delete(entity.id);
        }

        private selectEntity(entity: Entity): void {
            this.selectedIds.add(entity.id);
        }

        // Most bulk actions only apply to active entities, so a mixed selection is narrowed
        // before it is emitted rather than letting the handler patch rows it cannot affect.
        private get activeSelectedEntities(): Entity[] {
            return this.selectedEntities.filter((entity) => entity.active);
        }

        private get inactiveSelectedEntities(): Entity[] {
            return this.selectedEntities.filter((entity) => !entity.active);
        }

        private bulkActivateEntities(): void {
            this.$emit('setEntitiesActiveState', {
                entities: this.inactiveSelectedEntities,
                value: true,
                isBulk: true,
            });
        }

        private bulkDeactivateEntities(): void {
            this.$emit('setEntitiesActiveState', {
                entities: this.activeSelectedEntities,
                value: false,
                isBulk: true,
            });
        }

        private bulkSetEntitiesPauseStatus(value: boolean): void {
            this.$emit('setEntitiesPauseState', {
                // Entities already in the target pause state would patch to their own value.
                entities: this.activeSelectedEntities.filter((entity) => entity.paused !== value),
                isBulk: true,
                value,
            });
        }

        private get isEnabledSendDocumentRequest(): boolean {
            return this.activeSelectedEntities.some(isEligibleForNewLink);
        }

        private sendDocumentRequest(): void {
            this.$emit('sendDocumentRequest', this.activeSelectedEntities);
        }
    }
</script>
